import guideHero from "@/assets/yoga/guide-hero.jpg";
import imgBeginner from "@/assets/yoga/cat-beginner.jpg";
import imgGrounding from "@/assets/yoga/cat-grounding.jpg";
import imgAnxiety from "@/assets/yoga/cat-anxiety.jpg";
import imgTrauma from "@/assets/yoga/cat-trauma.jpg";
import imgEmotional from "@/assets/yoga/cat-emotional.jpg";
import imgSleep from "@/assets/yoga/cat-sleep.jpg";
import imgHealing from "@/assets/yoga/cat-healing.jpg";
import imgRecovery from "@/assets/yoga/cat-recovery.jpg";

export const YOGA_GUIDE_HERO = guideHero;

export type YogaPractice = {
  slug: string;
  title: string;
  duration: string;
  blurb: string;
  script: string[];
  safety: string[];
  modifications: string[];
  accessibility: string[];
};

export type YogaCategory = {
  id: string;
  title: string;
  emoji: string;
  description: string;
  image: string;
  benefits: string[];
  practices: YogaPractice[];
};

const choice = "You are always in control. You can pause, skip, modify, or stop any time.";
const noPain = "Stop or back off if anything causes pain, dizziness, or activation.";
const breathFirst = "If the breath becomes strained, return to natural breathing.";
const orient = "Keep eyes soft and open if closing them feels unsafe — orient to the room.";

export const YOGA_CATEGORIES: YogaCategory[] = [
  {
    id: "beginner",
    title: "Gentle Foundations",
    emoji: "🌱",
    description: "Welcoming first steps into trauma-informed movement.",
    image: imgBeginner,
    benefits: ["Builds trust with the body", "Gentle nervous system regulation", "Welcoming, no experience needed"],
    practices: [
      {
        slug: "chair-yoga-for-safety",
        title: "Chair Yoga for Safety",
        duration: "10 min",
        blurb:
          "Gentle seated movements for beginners, anxiety, trauma recovery, and limited mobility.",
        script: [
          "Sit toward the front of a sturdy chair. Feet flat on the floor, or supported by a book or cushion.",
          "Place hands on thighs. Notice the contact of the chair, the floor, your clothing.",
          "Inhale gently and lift the crown of the head a little; exhale and let the shoulders soften. Repeat 3 times at your own pace.",
          "On an inhale, raise one or both arms only as far as feels comfortable. Lower on the exhale. Repeat 3–5 times.",
          "Slow shoulder rolls: back 3 times, forward 3 times. Smaller circles are completely fine.",
          "Gentle neck movement: chin slightly toward chest, then lift. Then ear toward shoulder on each side. Stop anywhere it feels like enough.",
          "Seated side bend: one hand on the chair, the other arm reaches up or out. Two slow breaths each side.",
          "Place both hands on the belly. Take three quiet breaths and notice anything that feels different than when you began.",
        ],
        safety: [choice, noPain, "Skip the neck section entirely if it feels activating."],
        modifications: [
          "All arm lifts can be done as small lifts of the hands only.",
          "Keep eyes open and gaze soft if closing them feels unsafe.",
        ],
        accessibility: [
          "Designed for chairs, wheelchairs, or bed-sitting positions.",
          "Use a firm cushion behind the lower back for extra support.",
        ],
      },
      {
        slug: "first-steps-into-yoga",
        title: "First Steps into Yoga",
        duration: "8 min",
        blurb: "A welcoming introduction to trauma-informed movement.",
        script: [
          "Find any comfortable position — seated, standing, or lying down.",
          "Notice three points of contact between your body and what supports you.",
          "Take one slow breath in through the nose, and a soft sigh out through the mouth.",
          "Slowly rock side to side — small, easy movements — for 3 breaths.",
          "If standing or sitting tall: reach arms forward and gently sweep them open like opening curtains, twice.",
          "Add a tiny forward fold or chest opener — whichever your body invites.",
          "Return to stillness. Let the body settle for 30 seconds.",
          "Notice: there is nothing to perform. You arrived. That is the practice.",
        ],
        safety: [choice, "There is no right shape to make — let your body lead."],
        modifications: ["Every movement can be visualized only — imagined movement still regulates the nervous system."],
        accessibility: ["Works from bed, floor, chair, or standing."],
      },
      {
        slug: "breath-and-movement-basics",
        title: "Breath & Movement Basics",
        duration: "6 min",
        blurb: "Learn how breath and movement can work together to support regulation.",
        script: [
          "Sit or stand tall. Let your jaw soften.",
          "Inhale and lift the arms (or hands) a little; exhale and lower. Repeat 3 times.",
          "Inhale and open the chest; exhale and round the upper back gently. Repeat 4 times.",
          "Try matching the breath to the movement — but if they don't sync, choose breath first.",
          "Slow the exhale slightly: in for 4, out for 6. Three rounds.",
          "Rest the hands in the lap. Notice the breath without changing it.",
        ],
        safety: [breathFirst, "Breath holds are not used here intentionally."],
        modifications: ["You can do this lying down or with hands resting still."],
        accessibility: ["No equipment needed. Adaptable to any seated position."],
      },
      {
        slug: "gentle-stretch-and-release",
        title: "Gentle Stretch & Release",
        duration: "10 min",
        blurb: "Ease tension from the body with slow, supportive stretches.",
        script: [
          "Begin in any comfortable position. Three slow breaths to arrive.",
          "Interlace fingers and reach forward at chest height, rounding the upper back. Hold 3 breaths.",
          "Open the arms wide, gentle chest opener. 3 breaths.",
          "Slow seated or standing side bend, right then left. 3 breaths each side.",
          "Gentle twist: hand on opposite knee or thigh, look over the shoulder only as far as feels easy. 3 breaths each side.",
          "Forward fold (seated or standing) with bent knees if needed. Let the head be heavy. 4 breaths.",
          "Slow roll up to vertical, stacking one segment at a time.",
          "Pause. Place a hand somewhere comforting and rest.",
        ],
        safety: [noPain, "Avoid twists if you are pregnant or have recent abdominal injury."],
        modifications: ["All stretches can be 20% of the range that feels available."],
        accessibility: ["Chair-friendly throughout."],
      },
    ],
  },
  {
    id: "grounding",
    title: "Grounding & Safety",
    emoji: "🪨",
    description: "Build a felt sense of safety and connection to the earth.",
    image: imgGrounding,
    benefits: ["Felt sense of safety and stability", "Reconnects you to the present moment", "Anchors a regulated baseline"],
    practices: [
      {
        slug: "grounding-yoga-flow",
        title: "Grounding Yoga Flow",
        duration: "8 min",
        blurb: "Reconnect with your body and the present moment through simple postures.",
        script: [
          "Stand or sit with feet flat. Press down through all four corners of each foot.",
          "Look slowly around the room. Notice colors, shapes, light.",
          "Tall posture, hands at heart center. Three slow breaths.",
          "Mountain pose or seated tall: feel the line from feet to crown.",
          "Step or sway gently side to side — feel the weight shift.",
          "Forward fold with soft knees, let the head hang. 3 breaths.",
          "Slow rise. Bring hands to thighs and press down — feel the support of the ground.",
          "Whisper or think: I am here. The floor is here. This moment is here.",
        ],
        safety: [orient, "Skip forward fold if it brings dizziness."],
        modifications: ["All standing postures can be done seated."],
        accessibility: ["Bare feet optional. Wall support welcome."],
      },
      {
        slug: "rhythmic-grounding-flow",
        title: "Rhythmic Grounding Flow",
        duration: "10 min",
        blurb:
          "Repetitive, predictable movement for a restless system — rhythm first, grounding second, slowing down last.",
        script: [
          "Stand or sit tall. Let the arms hang or rest.",
          "Begin a steady sway: weight to the right, weight to the left. Find a rhythm that feels almost automatic.",
          "Add the arms — let them swing softly across the body as you sway.",
          "Change to marching: lift one heel, then the other, staying in the same rhythm. Continue for about a minute.",
          "Return to the sway and let it become slower and shorter.",
          "Press both feet down. Hold for three counts, then release. Twice.",
          "Rest your hands on your thighs. Feel the chair or floor holding you.",
          "Take three easy breaths, keeping your eyes open and soft.",
        ],
        safety: [choice, noPain, orient],
        modifications: [
          "Sway from the shoulders only if the legs are tired.",
          "Slow the rhythm at any time — there is no tempo to match.",
        ],
        accessibility: [
          "Fully available seated or in a wheelchair.",
          "A wall behind you can add a sense of support.",
        ],
      },
      {
        slug: "yoga-for-feeling-safe",
        title: "Yoga for Feeling Safe",
        duration: "12 min",
        blurb: "Movement focused on creating a felt sense of safety in the body.",
        script: [
          "Choose a position where your back feels protected — a wall, a chair, the floor.",
          "Notice one place in the body that already feels neutral or safe.",
          "Self-hug: wrap arms around yourself, give a gentle squeeze, breathe.",
          "Butterfly tap: alternate gentle taps on each shoulder for 1 minute.",
          "Slow cat-cow on hands and knees, or seated — 5 cycles. Move only as much as feels safe.",
          "Child's pose or seated forward fold with elbows on thighs. 5 breaths.",
          "Return to sitting. Notice your safe place again. Has it grown?",
        ],
        safety: ["Skip any pose that brings activation — there are always alternatives.", orient],
        modifications: ["Cat-cow can be done in a chair or against a wall."],
        accessibility: ["Use cushions liberally under hips, knees, head."],
      },
      {
        slug: "grounding-strong-energy",
        title: "Grounding Strong Energy",
        duration: "10 min",
        blurb:
          "For fight energy — anger, pressure or readiness to react. Steady, grounded movement that gives strong energy somewhere safe to go.",
        script: [
          "Stand near a wall, or sit tall with both feet on the floor. Notice where your body meets the ground.",
          "Press both feet down as if pushing the floor away. Hold for a slow count of five, then let go. Repeat three times.",
          "Face the wall, hands at chest height on the wall. Push steadily for five seconds — strong, not straining — then soften. Repeat four times at your own pace.",
          "Let the arms hang. Allow a small, easy shaking through the hands and forearms for 20–30 seconds. Stop whenever it feels like enough.",
          "Squeeze both fists slowly, hold for three, then open the fingers wide. Repeat five times, letting the release be slower than the squeeze.",
          "Slow half-circle with the arms: sweep out and up on an inhale, lower on a longer exhale. Three rounds.",
          "Stand or sit still for a moment. Notice the weight of your feet and the temperature of the air.",
          "Name one thing you can see in the room before you finish.",
        ],
        safety: [
          choice,
          noPain,
          "Strong energy is information, not a problem to fix. Nothing here is about forcing yourself calm.",
        ],
        modifications: [
          "The wall push can be done seated, pressing hands into a table or the arms of a chair.",
          "Skip the shaking entirely if it feels unsettling — the foot press alone is a complete practice.",
        ],
        accessibility: [
          "Works standing, seated or in a wheelchair with brakes on.",
          "Keep a chair or wall within reach for balance.",
        ],
      },
    ],
  },
  {
    id: "anxiety",
    title: "Anxiety & Stress Relief",
    emoji: "🌬️",
    description: "Soften activation, lengthen the exhale, return to regulation.",
    image: imgAnxiety,
    benefits: ["Softens activation and racing thoughts", "Lengthens the exhale for vagal tone", "Returns the body toward calm"],
    practices: [
      {
        slug: "yoga-for-anxiety",
        title: "Yoga for Anxiety",
        duration: "10 min",
        blurb: "Gentle movement and breathing to calm activation and racing thoughts.",
        script: [
          "Settle into any comfortable position. Lengthen the exhale: in 4, out 6.",
          "Roll the shoulders slowly back 5 times, then forward 5 times.",
          "Seated side bend, right then left. 3 breaths each.",
          "Slow seated twist, each side. 3 breaths each.",
          "Forward fold with soft knees or seated forward fold. 5 breaths, head heavy.",
          "Lie down or sit back. Knees bent or supported.",
          "Hand on belly: 8 slow breaths, exhale always longer than inhale.",
          "Rest. Notice the difference between when you began and now.",
        ],
        safety: [breathFirst, choice],
        modifications: ["If lying down feels too vulnerable, stay seated."],
        accessibility: ["Adaptable to bed, chair, or floor."],
      },
      {
        slug: "calm-the-nervous-system",
        title: "Calm the Nervous System",
        duration: "12 min",
        blurb: "A sequence designed to reduce stress and increase regulation.",
        script: [
          "Begin lying down with knees bent and feet flat, or any supported position.",
          "Hum softly on the exhale (voo or mmm sound) for 5 long exhales.",
          "Slow knee-to-chest, one leg at a time, 3 each side.",
          "Supported bridge: lift hips a few inches and slide a cushion under the sacrum. Rest 5 breaths.",
          "Lower hips. Knees fall side to side (windshield wipers) 6 times.",
          "Legs up the wall or on a chair seat, 2 minutes.",
          "Return to lying flat. Hand on heart. 5 slow breaths.",
          "Stay as long as you'd like in the rest.",
        ],
        safety: ["Skip humming if it feels exposing.", noPain],
        modifications: ["Legs up the wall can be replaced with legs on a chair, or skipped."],
        accessibility: ["Use blankets and pillows generously."],
      },
      {
        slug: "slow-the-rush",
        title: "Slow the Rush",
        duration: "10 min",
        blurb:
          "Flight-response support. Starts at the pace you are already moving and slows gradually — no sudden stillness required.",
        script: [
          "Begin by walking gently on the spot, or tapping the feet if you are seated. Let it be a little quicker than comfortable at first.",
          "Keep the movement going and let your arms swing loosely at your sides.",
          "After about a minute, let the pace drop slightly. Same movement, just a little slower.",
          "Add a long, unhurried exhale each time your right foot lands or taps.",
          "Slow again. Feel the pause at the top of each step.",
          "Let the stepping become a gentle sway, weight moving side to side.",
          "Let the sway become smaller and smaller until you are simply standing or sitting.",
          "Rest one hand somewhere comfortable and take three breaths. Movement is always available again if stillness is too much.",
        ],
        safety: [
          choice,
          breathFirst,
          "You never have to arrive at stillness. Staying in gentle movement is a complete practice.",
        ],
        modifications: [
          "Seated foot taps replace stepping entirely.",
          "Keep the arm swing small if the room is tight.",
        ],
        accessibility: [
          "Standing or seated. No floor work.",
          "Hold a chair or wall for balance at any point.",
        ],
      },
      {
        slug: "rest-and-reset-flow",
        title: "Rest & Reset Flow",
        duration: "15 min",
        blurb: "Slow movement to shift the body out of survival mode.",
        script: [
          "Lie on your back with knees bent, feet flat. Cushion under head if helpful.",
          "Hand on belly, hand on heart. 8 slow breaths.",
          "Knees fall to one side, head turns the other way — slow twist. 5 breaths each side.",
          "Hug knees to chest, rock gently side to side. 30 seconds.",
          "Supported bridge with cushion under sacrum. 1 minute.",
          "Legs extended, arms wide. Imagine the body melting into the floor. 1 minute.",
          "Roll to your side. Pause for 5 breaths before sitting up.",
          "Sit quietly for 1 minute. Notice the shift.",
        ],
        safety: ["Skip twists if pregnant or recovering from abdominal surgery.", choice],
        modifications: ["The whole sequence can be done in bed."],
        accessibility: ["Pillows under knees, head, or hips welcome throughout."],
      },
    ],
  },
  {
    id: "trauma-recovery",
    title: "Trauma Recovery",
    emoji: "💗",
    description: "Slow, choice-based practices that build trust with the body.",
    image: imgTrauma,
    benefits: ["Rebuilds trust and body awareness", "Choice-based, never forced", "Supports gentle emotional processing"],
    practices: [
      {
        slug: "somatic-yoga-for-trauma-recovery",
        title: "Somatic Yoga for Trauma Recovery",
        duration: "15 min",
        blurb: "Combines movement, breath, and body awareness to support healing.",
        script: [
          "Choose a position where you feel supported. Orient to the room with slow eye movement.",
          "Notice one neutral or pleasant sensation in the body. Stay with it for 3 breaths.",
          "Slow, small movements: gentle rocking, swaying, or rolling the head — whatever the body invites.",
          "Self-touch: place a hand on the chest, belly, or another comforting place. 5 breaths.",
          "Cat-cow or seated spinal wave, 5 slow cycles.",
          "Pause. Check in: do I need more movement, or more stillness?",
          "Follow that signal for 3 minutes.",
          "End with one hand on heart. Whisper: thank you, body.",
        ],
        safety: ["Pause anytime sensation grows beyond what feels workable.", orient],
        modifications: ["The entire practice can happen in stillness with micro-movements only."],
        accessibility: ["Floor, chair, or bed."],
      },
      {
        slug: "tiny-movements-to-reconnect",
        title: "Tiny Movements to Reconnect",
        duration: "8 min",
        blurb:
          "Freeze-response support. Eyes open, seated, and beginning with the smallest possible movements of the fingers and toes.",
        script: [
          "Sit supported in a chair. Keep your eyes open and let your gaze rest wherever it wants.",
          "Wiggle one finger. Just one. Then another.",
          "Slowly open and close one hand. Then the other. There is no pace to keep.",
          "Circle one wrist twice in each direction. Then the other wrist.",
          "Press the toes gently into the floor, then lift them. Three times.",
          "Circle one ankle, then the other, as small as you like.",
          "Lift one shoulder a centimetre, then let it drop. Repeat on the other side.",
          "Look slowly around the room and name one colour you can see.",
        ],
        safety: [
          choice,
          "Numbness is a protective response, not a failure. Small is enough.",
          orient,
        ],
        modifications: [
          "If nothing moves, imagining the movement counts.",
          "Do one section only and stop there.",
        ],
        accessibility: [
          "Entirely seated. Suitable for bed or wheelchair.",
          "No closed eyes, no floor poses.",
        ],
      },
      {
        slug: "orient-and-return",
        title: "Orient and Return",
        duration: "10 min",
        blurb:
          "Eyes-open orienting for a shut-down or disconnected system — noticing the room first, the body second.",
        script: [
          "Sit comfortably with your back supported and both feet in contact with the floor or a footrest.",
          "Let your eyes move slowly to the left, then back. Then to the right, then back.",
          "Turn your head slowly to follow your gaze in one direction. Pause. Return to centre.",
          "Repeat to the other side, moving no faster than your attention.",
          "Notice three objects in the room. Say their names quietly or in your mind.",
          "Bring one hand to the opposite shoulder and rest it there for a few breaths.",
          "Press both feet down, then release. Twice.",
          "Notice whether the room feels any different than when you started. Either answer is fine.",
        ],
        safety: [
          choice,
          "Keep eyes open throughout — this practice is built that way on purpose.",
          "Move slowly enough that the head turn never causes dizziness.",
        ],
        modifications: [
          "Move the eyes only, leaving the head still.",
          "Shorten to the orienting section alone.",
        ],
        accessibility: [
          "Seated or lying with head supported.",
          "Nothing here requires the floor.",
        ],
      },
      {
        slug: "building-capacity",
        title: "Building Capacity",
        duration: "12 min",
        blurb: "Develop greater nervous system resilience through mindful movement.",
        script: [
          "Begin with 5 slow breaths and one orienting glance around the room.",
          "Pendulation: notice one easeful sensation, then one slightly harder sensation, then back to ease. Repeat 3 times.",
          "Slow standing or seated sun breath: arms rise on the inhale, lower on the exhale. 5 rounds.",
          "Tree pose with wall support, or seated balance: one foot lifted gently. 30 seconds each side.",
          "Warrior II at a wall, or seated arms reaching wide. 4 breaths each side.",
          "Forward fold to gently rest. 5 breaths.",
          "Lie or sit and integrate. 1 minute of stillness.",
        ],
        safety: ["Stop pendulation if the harder sensation grows too loud.", noPain],
        modifications: ["All standing poses have wall and seated versions."],
        accessibility: ["Use a chair, wall, or even visualization."],
      },
      {
        slug: "boundaries-through-movement",
        title: "Boundaries Through Movement",
        duration: "10 min",
        blurb:
          "Practising the shape of a boundary in the body — reaching, holding a line, and returning to yourself.",
        script: [
          "Stand or sit tall with your feet grounded.",
          "Bring both palms forward at chest height, as if resting them on a pane of glass. Hold for three breaths.",
          "Press the palms gently forward on an exhale, then let them soften back. Three times.",
          "Extend one arm out to the side, palm facing away — a quiet, unhurried 'not right now'. Hold, then lower.",
          "Repeat on the other side, taking as long as you like.",
          "Cross both hands over your chest and rest them there for three breaths.",
          "Press the feet down and lengthen a little through the spine.",
          "Notice how it felt to take up space. Whatever came up is allowed.",
        ],
        safety: [
          choice,
          noPain,
          "Boundary gestures can bring up feeling. You can stop at any point and rest a hand on your chest instead.",
        ],
        modifications: [
          "Use one hand only.",
          "Make the gesture tiny — the size does not change its meaning.",
        ],
        accessibility: [
          "Seated and wheelchair friendly.",
          "No floor work or balance required.",
        ],
      },
    ],
  },
  {
    id: "emotional-regulation",
    title: "Emotional Regulation",
    emoji: "💛",
    description: "Move with — not against — what you're feeling.",
    image: imgEmotional,
    benefits: ["Move with feelings rather than against", "Cultivates self-compassion", "Restores emotional flexibility"],
    practices: [
      {
        slug: "heart-opening-practice",
        title: "Heart Opening Practice",
        duration: "8 min",
        blurb: "Cultivate compassion, openness, and emotional awareness.",
        script: [
          "Sit or stand tall. Soften the jaw and shoulders.",
          "Hands at heart center. Three slow breaths.",
          "Inhale, sweep arms wide and slightly back; exhale, hands return to heart. 5 rounds.",
          "Sphinx or seated chest lift: gentle backbend, only as much as feels safe. 4 breaths.",
          "Self-hug, squeeze, breathe. 4 breaths.",
          "Open arms wide one more time. Soften.",
          "Rest hands on heart. Whisper: may my heart stay tender and safe.",
        ],
        safety: ["Backbends can feel vulnerable — keep them small or skip them.", choice],
        modifications: ["Hands stay at heart for entire practice if backbends feel like too much."],
        accessibility: ["Fully chair-adaptable."],
      },
      {
        slug: "self-compassion-flow",
        title: "Self-Compassion Flow",
        duration: "10 min",
        blurb: "A nurturing sequence focused on kindness toward yourself.",
        script: [
          "Settle. Place one hand on heart, one on belly. 5 slow breaths.",
          "Sway gently side to side, as if rocking yourself. 30 seconds.",
          "Slow side bends, right then left — like a gentle tree. 3 breaths each side.",
          "Child's pose or seated fold with arms forward. 5 breaths.",
          "Sit up slowly. Self-hug for 4 breaths.",
          "Open arms wide. Receive imaginary kindness. Close arms back over heart.",
          "Repeat softly: I am here for myself.",
        ],
        safety: [choice],
        modifications: ["Practice entirely from a chair or bed."],
        accessibility: ["Use cushions for support in all postures."],
      },
      {
        slug: "choose-your-own-rhythm",
        title: "Choose Your Own Rhythm",
        duration: "12 min",
        blurb:
          "A practice built entirely out of choices — you decide the pace, the shape and when it ends.",
        script: [
          "Choose your position: seated, standing or lying down. That first choice is yours.",
          "Choose whether your eyes stay open or soften. Either is right.",
          "Move one shoulder however it wants to move for a few seconds. You decide the direction.",
          "Choose one arm. Move it slowly in any way that feels good, for as long as you like.",
          "Choose a pace — slower or a little quicker — and let the movement follow it.",
          "Choose one part of you that would like attention, and rest a hand there.",
          "Choose whether to keep going or to stop. There is no wrong answer.",
          "Before you finish, notice one preference you discovered today.",
        ],
        safety: [
          choice,
          "Being asked what you want can feel unfamiliar. Not knowing is a fine answer.",
          orient,
        ],
        modifications: [
          "If choosing feels hard, follow the order as written — that is also a choice.",
          "Any section can be skipped.",
        ],
        accessibility: [
          "Works in a chair, in bed, standing or on the floor.",
          "No equipment needed.",
        ],
      },
      {
        slug: "finding-your-space",
        title: "Finding Your Space",
        duration: "10 min",
        blurb:
          "Fawn-response support — marking out the space around you and letting your attention come back to your own body.",
        script: [
          "Stand or sit where you have a little room around you.",
          "Reach one arm out to the side as far as feels comfortable, then bring it back in toward your chest. Three times.",
          "Repeat on the other side, noticing how far your space extends.",
          "Reach both arms forward, then draw them back toward you, as if gathering something that is yours.",
          "Sweep one arm slowly around you, tracing the edge of your own space. Then the other.",
          "Rest both hands on your chest or belly and take three breaths that belong only to you.",
          "Ask yourself quietly: what would I like right now? You do not need an answer.",
          "Finish however you would like — that choice is part of the practice.",
        ],
        safety: [
          choice,
          "Turning toward others is a survival skill, not a flaw. Nothing here is about blame.",
          orient,
        ],
        modifications: [
          "Keep the reaches small if space is limited.",
          "Do it seated with the arms alone.",
        ],
        accessibility: [
          "Standing, seated or wheelchair.",
          "Only one arm needs to move for the practice to work.",
        ],
      },
    ],
  },
  {
    id: "sleep",
    title: "Sleep & Deep Rest",
    emoji: "🌙",
    description: "Slow the system, soften the body, prepare for rest.",
    image: imgSleep,
    benefits: ["Slows the system for restful sleep", "Activates rest-and-digest", "Eases bedtime activation"],
    practices: [
      {
        slug: "bedtime-yoga",
        title: "Bedtime Yoga",
        duration: "10 min",
        blurb: "Prepare your body and mind for restful sleep.",
        script: [
          "In bed or on the floor: knees bent, feet flat. Three slow breaths.",
          "Hug one knee gently to chest, 3 breaths. Switch sides.",
          "Both knees to chest (or imagined), slow rocking, 30 seconds.",
          "Knees fall to one side, slow twist, head looks the other way. 4 breaths each side.",
          "Legs up the wall, headboard, or pillow stack. 2 minutes.",
          "Lie flat. Hand on belly. Lengthen exhale gently. 1 minute.",
          "Drift, rest, sleep.",
        ],
        safety: ["Skip twists if pregnant or recovering from abdominal injury.", choice],
        modifications: ["Skip legs up the wall — rest legs over a pillow."],
        accessibility: ["Designed to be done fully in bed."],
      },
      {
        slug: "evening-wind-down",
        title: "Evening Wind Down",
        duration: "12 min",
        blurb: "Gentle stretches and breathing to close the day.",
        script: [
          "Sit comfortably. Three slow breaths.",
          "Slow neck movements: chin to chest, ear to shoulder both sides. Move only where it feels safe.",
          "Shoulder rolls back 5, forward 5.",
          "Seated side bend each side, 3 breaths each.",
          "Seated forward fold, head heavy. 5 breaths.",
          "Move to lying down. Knees bent. Hands on belly.",
          "Lengthen exhale (in 4, out 6) for 2 minutes.",
          "Rest. Let the day end.",
        ],
        safety: [breathFirst, "Skip neck movements if activating."],
        modifications: ["Stay seated the entire time if lying down feels too vulnerable."],
        accessibility: ["Adaptable to bed."],
      },
      {
        slug: "restorative-yoga-for-stress-relief",
        title: "Restorative Yoga for Stress Relief",
        duration: "15 min",
        blurb: "Supported postures that activate rest-and-digest responses.",
        script: [
          "Gather pillows, blankets, or rolled towels.",
          "Supported child's pose: knees wide, pillow under torso. 3 minutes.",
          "Slow rise. Supported reclined butterfly: feet together, knees out, pillows under each thigh. 3 minutes.",
          "Roll to side. Supine twist with cushion between knees, 2 minutes each side.",
          "Final rest: lie back with eye covering if desired. 4 minutes.",
          "Notice the stillness before you rise.",
        ],
        safety: ["Skip any posture that feels exposing — restorative does not mean defenseless."],
        modifications: ["Each pose can be shortened or done in a chair with pillows."],
        accessibility: ["Use as many props as you'd like. There is no minimum."],
      },
      {
        slug: "yoga-nidra-for-deep-rest",
        title: "Yoga Nidra for Deep Rest",
        duration: "20 min",
        blurb: "A guided relaxation practice supporting nervous system recovery.",
        script: [
          "Lie down, fully supported. Eyes closed or softly open.",
          "Set an intention — a simple word like 'rest' or 'safe.'",
          "Body scan: bring attention slowly through feet, legs, hips, belly, chest, arms, hands, neck, face. Move at the pace of breath.",
          "Notice the breath, not changing it. 1 minute.",
          "Notice sensations — warmth, weight, temperature. 1 minute.",
          "Notice emotions — name them gently and let them pass. 1 minute.",
          "Imagine a place of safety. Stay there for 2 minutes.",
          "Return slowly: breath, body, room, eyes. Move gently when you rise.",
        ],
        safety: [
          "Yoga Nidra can surface emotion or memory — open eyes and orient anytime.",
          orient,
        ],
        modifications: ["Keep eyes open the entire time if needed."],
        accessibility: ["Best done in bed or fully supported on the floor."],
      },
      {
        slug: "sleep-preparation-body-flow",
        title: "Sleep Preparation Body Flow",
        duration: "10 min",
        blurb: "Release tension before bed.",
        script: [
          "Stand or sit tall. Slow shoulder rolls, 5 each direction.",
          "Gentle neck movements (only safe ranges), 3 each direction.",
          "Standing or seated forward fold. 5 breaths.",
          "Lie in bed. Knees to chest, slow rocking, 30 seconds.",
          "Windshield wiper knees, 6 times.",
          "Final rest: hand on belly. Long exhales for 2 minutes.",
        ],
        safety: [noPain, choice],
        modifications: ["Skip standing — go straight to bed."],
        accessibility: ["Bed-friendly throughout."],
      },
    ],
  },
  {
    id: "healing-movement",
    title: "Movement & Release",
    emoji: "🌿",
    description: "Release where the body holds the day.",
    image: imgHealing,
    benefits: ["Releases tension held in the body", "Restores mobility and ease", "Moves stagnant stress safely"],
    practices: [
      {
        slug: "full-body-release-flow",
        title: "Full Body Release Flow",
        duration: "15 min",
        blurb: "A gentle whole-body sequence supporting relaxation and mobility.",
        script: [
          "Stand or sit tall. Three slow breaths.",
          "Slow neck releases, 3 each direction.",
          "Shoulder rolls back and forward, 5 each.",
          "Cat-cow or seated spinal wave, 6 rounds.",
          "Standing or seated forward fold, 5 breaths.",
          "Gentle twist each side, 4 breaths each.",
          "Hip circles, 5 each direction.",
          "Calf and ankle rolls, 5 each direction.",
          "Lie down. Full body stretch, then soften.",
          "Rest in stillness for 2 minutes.",
        ],
        safety: [noPain, choice],
        modifications: ["All standing poses can be done seated."],
        accessibility: ["Chair-friendly throughout."],
      },
      {
        slug: "shoulder-and-neck-release",
        title: "Shoulder & Neck Release",
        duration: "8 min",
        blurb: "Reduce tension commonly held during stress and trauma.",
        script: [
          "Sit or stand tall. Three slow breaths into the upper back.",
          "Shoulder rolls back 5, forward 5.",
          "Lift shoulders to ears on inhale, drop on exhale. 5 times.",
          "Ear to shoulder, right then left — only safe ranges. 3 breaths each.",
          "Chin to chest, then lift. 3 slow rounds.",
          "Eagle arms or simple shoulder hug. 4 breaths each side.",
          "Hands on shoulders, slow elbow circles. 5 each direction.",
          "Rest. Notice the softening.",
        ],
        safety: ["Skip neck movements that bring activation or dizziness."],
        modifications: ["All movements can be smaller — even tiny."],
        accessibility: ["Wheelchair- and chair-friendly."],
      },
      {
        slug: "hip-opening-for-emotional-release",
        title: "Hip Opening for Emotional Release",
        duration: "12 min",
        blurb: "Trauma-sensitive movement focused on awareness, choice, and gentle release.",
        script: [
          "Sit comfortably. Three slow breaths into the hips.",
          "Seated hip circles, 5 each direction.",
          "Seated butterfly (soles together, knees wide) with pillow support under thighs. 5 breaths.",
          "Figure-4 stretch: ankle on opposite thigh, seated or lying. 5 breaths each side.",
          "Pigeon at the wall, on a chair, or low to the ground — your choice. 5 breaths each side.",
          "Reclined or seated butterfly to close. 1 minute.",
          "Lie or sit still. Notice emotion or sensation that may have surfaced.",
          "Hand on belly or heart. Five slow breaths.",
        ],
        safety: [
          "Hip openers may surface emotion — pause, ground, and reach for support if needed.",
          "Avoid deep hip stretches if pregnant or recovering from hip injury.",
        ],
        modifications: ["All hip openers have chair-based versions."],
        accessibility: ["Cushions and props welcome throughout."],
      },
      {
        slug: "step-sway-and-settle",
        title: "Step, Sway and Settle",
        duration: "10 min",
        blurb:
          "Flight-response support built on stepping and swaying — repetitive movement that gradually settles rather than stops.",
        script: [
          "Find a space where you can take one step forward and one step back, or tap the feet if seated.",
          "Step forward with one foot, then back. Then the other side. Keep it unhurried and even.",
          "Let the arms move naturally with the steps.",
          "Continue for a minute or two, noticing the predictable rhythm of forward and back.",
          "Change to a side-to-side sway, letting the head follow softly.",
          "Slow the sway by half. Then slow it again.",
          "Come to a comfortable standing or seated position with the feet grounded.",
          "Place a hand on the belly or the chair arm and take three breaths at your own pace.",
        ],
        safety: [
          choice,
          noPain,
          "If stopping feels uncomfortable, keep swaying gently — that is allowed.",
        ],
        modifications: [
          "Seated: alternate heel lifts instead of steps.",
          "Reduce the step length to a few inches.",
        ],
        accessibility: [
          "Needs about a metre of clear space, or none at all if seated.",
          "Keep a chair nearby for balance.",
        ],
      },
      {
        slug: "push-and-release",
        title: "Push and Release",
        duration: "12 min",
        blurb:
          "Fight-response support using steady wall pushes and slow releases — effort that has a clear beginning and a clear end.",
        script: [
          "Set yourself up an arm's length from a wall, or seated with hands on a sturdy table.",
          "Place both palms on the wall. Inhale. On the exhale, push steadily for five seconds. Release slowly and let the arms hang.",
          "Repeat the push three more times, making each release slower than the one before.",
          "Press one hand into the wall while the other rests on your chest or belly. Push, then release. Swap sides.",
          "Interlace the fingers and press the palms together in front of the chest. Hold five seconds. Release and shake the hands out gently.",
          "Press the feet down into the floor while the shoulders stay soft. Hold, then let go.",
          "Stand or sit with arms loose. Take three unhurried breaths and notice what has changed, or has not changed. Both are fine.",
          "Finish by looking slowly around the room.",
        ],
        safety: [
          choice,
          noPain,
          "This is about controlled effort, never striking, punching or forcing.",
        ],
        modifications: [
          "Every push can be done seated against a table, chair arms or your own thighs.",
          "Shorten the holds to two seconds if five feels like too much.",
        ],
        accessibility: [
          "No floor work. Chair or wheelchair friendly throughout.",
          "A cushion behind the lower back can help the seated version.",
        ],
      },
    ],
  },
  {
    id: "recovery-resilience",
    title: "Recovery & Resilience",
    emoji: "💪",
    description: "Reclaim strength, confidence, and rhythm.",
    image: imgRecovery,
    benefits: ["Reclaims strength and confidence", "Builds nervous-system resilience", "Reconnects you to vitality"],
    practices: [
      {
        slug: "gentle-awakening",
        title: "Gentle Awakening",
        duration: "10 min",
        blurb:
          "A slow, chair-supported way back into the body when everything feels heavy, distant or switched off.",
        script: [
          "Sit toward the front of a chair, or stay in bed with your back supported. Eyes open.",
          "Look around the space unhurriedly, letting your attention land wherever it lands.",
          "Rub the palms together slowly until you notice warmth, then rest them on your thighs.",
          "Gently squeeze one forearm, then the other, giving your body a little pressure and information.",
          "Roll the shoulders backwards three times, as small as needed.",
          "Lift the heels, then the toes, alternating slowly ten times.",
          "Reach one arm forward as far as is comfortable, then draw it back to your chest. Repeat on the other side.",
          "Sit for a moment and notice one part of you that feels slightly more present.",
        ],
        safety: [
          choice,
          "Heaviness is not laziness. Doing one step is a whole practice.",
          orient,
        ],
        modifications: [
          "Everything can be done lying down with head and knees supported.",
          "Use touch and warmth only if movement feels like too much.",
        ],
        accessibility: [
          "Chair, bed or wheelchair. No standing required.",
          "Blanket over the legs is welcome.",
        ],
      },
      {
        slug: "strong-feet-soft-body",
        title: "Strong Feet, Soft Body",
        duration: "12 min",
        blurb:
          "Fight-response support: firm through the legs, soft through the jaw, shoulders and hands. Strength without bracing.",
        script: [
          "Stand with feet hip-width apart, or sit with both feet flat and supported.",
          "Spread the toes. Press through the heels and the balls of the feet. Notice the legs becoming steady.",
          "Keeping the legs strong, let the shoulders drop away from the ears. Unclench the jaw. Let the tongue rest.",
          "Slow, small knee bends: lower a few inches, rise back up. Five times, at whatever depth suits today.",
          "Sway your weight side to side between the feet, like a slow metronome, for about 30 seconds.",
          "Bring the hands to the hips or the chair arms. Press down gently and lengthen through the spine for three breaths.",
          "Let the arms hang and allow a soft shake through the hands, then stop and stand still.",
          "Notice the difference between the strong parts and the soft parts of you.",
        ],
        safety: [
          choice,
          noPain,
          "Balance work near a wall or chair. Stop if you feel light-headed.",
        ],
        modifications: [
          "The whole sequence works seated — press the feet down instead of standing.",
          "Hold a chair back for any standing portion.",
        ],
        accessibility: [
          "Standing, seated and wheelchair options throughout.",
          "Shoes or bare feet — whichever feels more secure.",
        ],
      },
      {
        slug: "moving-with-confidence",
        title: "Moving with Confidence",
        duration: "10 min",
        blurb: "Reconnect with trust in your body.",
        script: [
          "Stand or sit tall. Plant feet wide and steady.",
          "Slow neck and shoulder warm-up, 1 minute.",
          "Sun breath with strong reach upward, 5 rounds.",
          "Warrior I or seated arm reach, 3 breaths each side.",
          "Open arms wide, palms forward (gentle 'here I am' gesture), 3 breaths.",
          "Soft bouncing from the knees or seated bounce, 30 seconds.",
          "Hands at heart. Notice: my body can move me through today.",
        ],
        safety: [noPain, "Use wall support if balance feels uncertain."],
        modifications: ["Seated version available for every pose."],
        accessibility: ["Wheelchair-friendly."],
      },
      {
        slug: "recovery-flow",
        title: "Recovery Flow",
        duration: "15 min",
        blurb: "A complete trauma-informed sequence for healing and regulation.",
        script: [
          "Settle. Orient. Three slow breaths.",
          "Sun breath, 5 rounds.",
          "Cat-cow or seated spinal wave, 6 rounds.",
          "Standing or seated forward fold, 5 breaths.",
          "Tree pose at a wall, 30 seconds each side.",
          "Gentle twist each side, 4 breaths.",
          "Reclined butterfly with pillow support, 2 minutes.",
          "Legs up the wall or on a chair, 2 minutes.",
          "Final rest with hand on heart. 2 minutes.",
        ],
        safety: [noPain, "Skip twists if pregnant or healing abdominal injury."],
        modifications: ["Use props generously. Stay seated throughout if needed."],
        accessibility: ["Adaptable to bed, chair, or floor."],
      },
    ],
  },
];
